import React from 'react';

import { TableFoldButton } from '@components/admin/booking/TableFoldButton';
import { Checkbox } from '@components/Table/Checkbox';
import { moneyStrings } from '@data/money';
import { paymentSlipStrings } from '@data/paymentSlip';
import { PaymentSlip, PaymentSlipHelper } from '@models/next/PaymentSlip';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate, formatDateTime } from '@utils/types';
import type { Checklist } from '@utils/useChecklist';
import { useLocalStorageState } from '@utils/useLocalStorageState';
import clsx from 'clsx';
import z, { type ZodTypeAny } from 'zod';

const strings = createStrings({
  ja: {
    details: '詳細',
    deadline: '期限',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  paymentSlipTable: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: '40px 40px 1fr 1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 60px',
    '& > div > div': {
      padding: '0.75rem',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '&$folded': {
      gridTemplateColumns: '40px 40px 1fr 1fr 1fr 1fr 1.5fr repeat(4, 1fr) 60px',
    },
  },
  paymentSlipDetailsHead: {
    whiteSpace: 'pre',
    backgroundColor: theme.palette.base[200],
  },
  paymentSlipDetailsHeadWithBreak: {
    whiteSpace: 'pre',
    ...common.exSmallerText,
    ...common.textGray500,
    backgroundColor: theme.palette.base[200],
    paddingTop: '0.5rem !important',
    paddingBottom: '0 !important',
  },
  paymentSlipDetailsCell: {
    ...common.textGray700,
    '&$error': {
      ...common.textError,
    },
    '&$statusTime': {
      paddingTop: 6,
      paddingBottom: 0,
    },
    '&$subsum': {
      ...common.textGray500,
    },
  },
  statusTime: {},
  error: {},
  statusTimeTag: {
    ...common.textGray500,
    '$error &': {
      ...common.textError,
    },
    ...common.exSmallerText,
  },
  subsum: {},
  folded: {},
}));

export interface PaymentSlipTableProps {
  items: PaymentSlip[];
  checklist: Checklist<number>;
}

export const PaymentSlipTable: React.FC<PaymentSlipTableProps> = ({ items, checklist }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [folded, setFolded] = useLocalStorageState('PAYMENT_SLIP_FOLDED', z.boolean(), false);

  const { shape } = PaymentSlip;

  const renderHead = (t?: ZodTypeAny, element?: React.ReactNode) => {
    return (
      <div className={classes.paymentSlipDetailsHead}>{t?.getFormMeta().label ?? element}</div>
    );
  };

  const renderHeadWithBreak = (head: string, breakAt: number) => {
    return (
      <div className={classes.paymentSlipDetailsHeadWithBreak}>
        {head.slice(0, breakAt)}
        <br />
        {head.slice(breakAt)}
      </div>
    );
  };

  const renderData = (data: React.ReactNode, subsum: boolean = false) => {
    return (
      <div
        className={clsx(
          classes.paymentSlipDetailsCell,
          classes.whitespacePre,
          subsum && classes.subsum,
        )}
      >
        {data}
      </div>
    );
  };

  return (
    <div style={{ overflow: 'scroll' }}>
      <div className={clsx(classes.paymentSlipTable, folded && classes.folded)}>
        <div style={{ display: 'contents' }}>
          {renderHead(
            undefined,
            <Checkbox
              value={checklist.allCheckedAndNotEmpty}
              onChange={checklist.toggleCheckAll}
            />,
          )}
          {renderHead(undefined, paymentSlipStrings.no)}

          {renderHead(shape.payment_slip_no)}
          {renderHead(shape.fc_payment_slip_no)}
          {renderHead(shape.pre_alert.shape.job_code)}
          {renderHead(shape.payee)}
          {renderHead(shape.payment_date)}
          {renderHead(shape.cutoff_date)}

          {renderHead(
            undefined,
            <>
              {shape._total_amount.getFormMeta().label}
              <span style={{ marginLeft: 5 }} />
              <TableFoldButton folded={folded} onValueChange={setFolded} />
            </>,
          )}

          {!folded && (
            <>
              {renderHeadWithBreak(paymentSlipStrings.subsums.tax_excluded_total_price, 2)}
              {renderHeadWithBreak(paymentSlipStrings.subsums.excise_tax, 2)}
              {renderHeadWithBreak(paymentSlipStrings.subsums.tatekae_total_price, 2)}
            </>
          )}

          {renderHead(shape.status)}
          {renderHead(shape.payment_status)}
          {renderHead()}
        </div>

        {items.map((item, i) => {
          const helper = new PaymentSlipHelper(item);
          const lastStatus = helper.getLastStatus();
          const lastPaymentStatus = helper.getLastPaymentStatus();

          return (
            <div key={item.id} className={classes.contentsHoverTextPrimary}>
              {renderData(
                <Checkbox
                  value={checklist.checked(item.id)}
                  onChange={() => checklist.toggle(item.id)}
                />,
              )}
              {renderData(i + 1)}
              {renderData(item.payment_slip_no)}
              {renderData(item.fc_payment_slip_no)}
              {renderData(item.pre_alert.job_code)}
              {renderData(item.payee.name_en)}
              {renderData(formatDate(item.payment_date))}
              {renderData(formatDate(item.cutoff_date))}

              {renderData(moneyStrings.formatYenFront(helper.getTotalAmount()))}

              {!folded && (
                <>
                  {renderData(moneyStrings.formatYenFront(helper.getTaxExcludedTotalPrice()), true)}
                  {renderData(moneyStrings.formatYenFront(helper.getExciseTax()), true)}
                  {renderData(moneyStrings.formatYenFront(helper.getTatekaeTotalPrice()), true)}
                </>
              )}

              <div
                className={clsx(
                  classes.paymentSlipDetailsCell,
                  classes.whitespacePre,
                  classes.statusTime,
                  lastStatus?.name === '修正中' && classes.error,
                )}
              >
                {lastStatus && (
                  <>
                    {lastStatus.name}

                    <div className={classes.statusTimeTag}>{formatDateTime(lastStatus.time)}</div>
                  </>
                )}
              </div>

              <div
                className={clsx(
                  classes.paymentSlipDetailsCell,
                  classes.whitespacePre,
                  classes.statusTime,
                  lastPaymentStatus?.name === '未' && classes.error,
                )}
              >
                {lastPaymentStatus && (
                  <>
                    {lastPaymentStatus?.name}

                    <div className={classes.statusTimeTag}>
                      {formatDateTime(lastPaymentStatus?.time)}
                    </div>
                  </>
                )}
              </div>

              <div className={classes.paymentSlipDetailsCell}>
                <a
                  href={
                    url('/admin/booking/PaymentSlipDetails') +
                    '?' +
                    new URLSearchParams({
                      id: String(item.id),
                      d2d_no: item.booking_inquiry.d2d_no,
                    }).toString()
                  }
                  target="_blank"
                  rel="noreferrer noopener"
                  className={classes.link}
                >
                  {strings.details}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
